import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Timer, Loader2, CreditCard, Sparkles, MessageSquareHeart, ShieldCheck, Gauge } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";

export default function AiMinutes() {
  const [info, setInfo] = useState(null);
  const [loadingBuy, setLoadingBuy] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/ai-minutes/me").then((r) => setInfo(r.data)).catch((err) => toast.error(apiError(err.response?.data?.detail)));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      if (tries > 8) { toast.error("No pudimos confirmar el pago. Si se cobró, contáctanos."); params.delete("session_id"); setParams(params, { replace: true }); return; }
      tries += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("¡Pago confirmado! Tus minutos IA se acreditaron. 🎉");
          load();
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired") { toast.error("La sesión de pago expiró."); params.delete("session_id"); setParams(params, { replace: true }); return; }
      } catch (e) { console.warn("Reintentando verificación de pago:", e?.message); }
      setTimeout(poll, 2500);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (id) => {
    setLoadingBuy(id);
    try {
      const { data } = await api.post("/ai-minutes/checkout/session", { package_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setLoadingBuy(null);
    }
  };

  const balance = info?.balance_minutes ?? 0;
  const used = info?.used_minutes ?? 0;
  const maxMin = info?.chat_max_minutes ?? 5;

  return (
    <div data-testid="ai-minutes-page">
      <div className="flex items-center gap-3 mb-2">
        <Bot className="text-yellow-400" size={26} />
        <h1 className="font-display font-black tracking-tight text-3xl">Minutos IA</h1>
      </div>
      <p className="text-zinc-400 mb-6">Respuesta Especializada de IA: tu reclutador y vendedor que platica con cada prospecto.</p>

      {/* Explicación del servicio */}
      <div data-testid="ai-minutes-explainer" className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-600/10 to-transparent p-6 mb-7">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquareHeart className="text-purple-400" size={20} />
          <h2 className="font-display font-bold text-lg">¿Qué es la Respuesta Especializada de IA?</h2>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          Activa un <strong className="text-white">asesor de IA especializado</strong> en tu campaña: conoce tu empresa,
          tus productos/servicios y tu plan de ganancias, y <strong className="text-white">charla en vivo con cada prospecto</strong> que
          llega a tu página de captura. Su misión es una sola: <strong className="text-yellow-impact">reclutar al prospecto o venderle</strong> lo
          que promocionas, con un poder de convencimiento de experto.
        </p>
        <ul className="mt-4 space-y-2 text-sm text-zinc-300">
          <li className="flex items-start gap-2"><Sparkles size={15} className="text-purple-400 mt-0.5 shrink-0" /> Se enfoca SOLO en tu oportunidad; nunca se desvía a otros temas.</li>
          <li className="flex items-start gap-2"><Gauge size={15} className="text-purple-400 mt-0.5 shrink-0" /> Si detecta poco interés o evasivas, cierra la charla con cortesía (ese prospecto solo queda en tu estadística).</li>
          <li className="flex items-start gap-2"><Timer size={15} className="text-purple-400 mt-0.5 shrink-0" /> Cada charla dura máximo {maxMin} minutos. Al calificar a un prospecto, le muestra tu WhatsApp para que te contacte.</li>
          <li className="flex items-start gap-2"><ShieldCheck size={15} className="text-purple-400 mt-0.5 shrink-0" /> 100% legal y ético: sin promesas de ingresos garantizados.</li>
        </ul>
      </div>

      {/* Recuadro de saldo */}
      <div data-testid="ai-minutes-balance" className="rounded-2xl border border-white/10 bg-[#121212] p-6 mb-3">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl bg-black/40 border border-green-500/20 p-5">
            <div className="flex items-center gap-2 text-green-400 text-xs uppercase tracking-wider font-bold mb-1">
              <Timer size={15} /> Minutos disponibles
            </div>
            <div data-testid="minutes-available" className="font-display font-black text-4xl text-white">{balance.toFixed(1)}<span className="text-lg text-zinc-500 ml-1">min</span></div>
            <p className="text-xs text-zinc-500 mt-1">Listos para usarse con tus prospectos</p>
          </div>
          <div className="rounded-xl bg-black/40 border border-purple-500/20 p-5">
            <div className="flex items-center gap-2 text-purple-300 text-xs uppercase tracking-wider font-bold mb-1">
              <Gauge size={15} /> Minutos consumidos
            </div>
            <div data-testid="minutes-used" className="font-display font-black text-4xl text-white">{used.toFixed(1)}<span className="text-lg text-zinc-500 ml-1">min</span></div>
            <p className="text-xs text-zinc-500 mt-1">Tiempo total de charlas IA realizadas</p>
          </div>
        </div>
      </div>

      <Button
        data-testid="buy-ai-minutes-cta"
        onClick={() => document.getElementById("ai-minutes-packs")?.scrollIntoView({ behavior: "smooth" })}
        className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl py-6 text-base mb-8">
        <CreditCard size={18} className="mr-2" /> COMPRAR MINUTOS IA
      </Button>

      {/* Paquetes */}
      <div id="ai-minutes-packs" className="scroll-mt-6">
        <h2 className="font-display font-bold text-xl mb-1">Elige tu paquete de minutos</h2>
        <p className="text-sm text-zinc-400 mb-5">Precio: ${info?.price_per_minute ?? 0.05} USD por minuto.</p>
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {(info?.packages || []).map((pkg) => (
            <div key={pkg.id} data-testid={`minutes-pack-${pkg.id}`} className={`relative rounded-2xl border p-6 flex flex-col ${pkg.popular ? "border-yellow-400 bg-yellow-400/5" : "border-white/10 bg-[#121212]"}`}>
              {pkg.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">Más popular</span>}
              <h3 className="font-display font-bold text-2xl">{pkg.label}</h3>
              <p className="text-sm text-zinc-400 mt-1">{pkg.tagline}</p>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display font-black text-4xl">${pkg.price % 1 === 0 ? pkg.price : pkg.price.toFixed(2)}</span>
                <span className="text-zinc-500 mb-1">USD</span>
              </div>
              <div className="mt-2 text-sm text-zinc-300 flex items-center gap-2"><Timer size={15} className="text-purple-400" /> {pkg.minutes} minutos de charla IA</div>
              <Button data-testid={`buy-pack-${pkg.id}`} onClick={() => buy(pkg.id)} disabled={loadingBuy === pkg.id}
                className={`mt-6 w-full rounded-xl py-6 font-bold ${pkg.popular ? "bg-yellow-400 hover:bg-yellow-300 text-black" : "bg-purple-600 hover:bg-purple-500 text-white"}`}>
                {loadingBuy === pkg.id ? <Loader2 className="animate-spin mr-2" size={16} /> : <CreditCard size={16} className="mr-2" />} Comprar
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
